import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Deck } from '../../types/pokemon';
import { DeckPreviewCards } from './DeckPreviewCards';
import { Clock, Users, Tag, Star, ArrowRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from '../../hooks/useTranslation';

interface DeckListItemProps {
  deck: Deck & { 
    avg_rating?: number;
    total_votes?: number;
    rating_score?: number;
    ratings?: Array<{
      rating_score: number;
      total_votes: number;
    }>;
    tags?: string[];
    user?: {
      username: string;
      avatar_url?: string;
    }
  };
  showAuthor?: boolean;
}

export function DeckListItem({ deck, showAuthor = true }: DeckListItemProps) {
  const { t, lang, getPathForLang } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  
  // Debug: Afficher la structure des données de deck dans la console
  useEffect(() => {
    console.log('Deck data:', { 
      id: deck.id, 
      avg_rating: deck.avg_rating, 
      total_votes: deck.total_votes, 
      rating_score: deck.rating_score,
      ratings: deck.ratings 
    });
  }, [deck]);
  
  // Vérifier la taille de l'écran au montage et lors des redimensionnements
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    // Vérification initiale
    checkMobile();
    
    // Ajouter un écouteur pour les redimensionnements
    window.addEventListener('resize', checkMobile);
    
    // Nettoyer l'écouteur lors du démontage
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Limiter les tags à afficher en mode mobile
  const maxDisplayTags = isMobile ? 2 : 3;
  const tags = deck.tags || [];
  const hasTags = tags.length > 0;
  const displayTags = hasTags ? tags.slice(0, maxDisplayTags) : [];
  const extraTagsCount = hasTags ? Math.max(0, tags.length - maxDisplayTags) : 0;

  // Calculs pour la notation en essayant toutes les sources possibles
  const rating = deck.ratings?.[0]?.rating_score || deck.rating_score || deck.avg_rating || 0;
  const totalVotes = deck.ratings?.[0]?.total_votes || deck.total_votes || 0;
  
  // Pour l'affichage de la note sur 5 étoiles
  const displayRating = (rating > 5 ? rating / 20 : rating).toFixed(1);

  return (
    <div className="relative bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-indigo-200 group">
      <Link to={getPathForLang(`/decks/${deck.id}`)} className="block h-full flex flex-col">
        {/* En-tête plus sobre avec le nom du deck */}
        <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 px-2 sm:px-3 py-2 border-b border-indigo-100">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-indigo-800 truncate max-w-[75%] group-hover:text-indigo-600 transition-colors">
              {deck.name}
            </h3>
            
            {/* Photo de profil en haut à droite */}
            {showAuthor && (
              <div className="flex-shrink-0">
                {(deck.user?.avatar_url || deck.profiles?.avatar_url) ? (
                  <img 
                    src={deck.user?.avatar_url || deck.profiles?.avatar_url || ''} 
                    alt="Avatar" 
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-indigo-100 shadow-sm"
                  />
                ) : (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border border-indigo-100 flex items-center justify-center shadow-sm">
                    <Users className="w-4 h-4 text-indigo-500" aria-hidden="true" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="p-2 sm:p-4 flex-1 flex flex-col">
          {/* Informations sur l'auteur, date et note */}
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2 sm:mb-3 justify-between">
            <div className="flex items-center gap-1 sm:gap-2">
              {showAuthor && (
                <span className="text-xs sm:text-sm font-medium text-indigo-700">
                 {t('public_decks.deck_details.created_by')} {deck.user?.username || deck.profiles?.username || t('public_decks.deck_details.user_fallback')}
                </span>
              )}
              
              <span className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full">
                <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 inline mr-0.5 sm:mr-1 text-gray-400" aria-hidden="true" />
                {formatDistanceToNow(new Date(deck.updated_at), { locale: lang === 'fr' ? fr : undefined })}
              </span>
            </div>
            
            {/* Badge de note */}
            <div className="flex items-center bg-indigo-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
              <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-yellow-500 mr-0.5 sm:mr-1" />
              <span className="text-[10px] sm:text-xs font-medium text-indigo-700">{displayRating}/5</span>
              {totalVotes > 0 && (
                <span className="text-[10px] sm:text-xs text-gray-500 ml-0.5 sm:ml-1">
                  ({totalVotes})
                </span>
              )}
            </div>
          </div>
          
          {/* Aperçu des cartes du deck */}
          <div className="bg-gray-50 p-1.5 sm:p-2 rounded-lg mb-2 sm:mb-3 flex-grow">
            <DeckPreviewCards 
              cards={deck.cards || []} 
              maxCards={isMobile ? 7 : (deck.cards && deck.cards.length > 10 ? 9 : 10)}
              mobileMode={isMobile}
              totalUniqueCards={deck.cards?.length || 0}
            />
          </div>
          
          {/* Section du bas: tags uniquement */}
          <div className="mt-auto">
            {/* Tags du deck */}
            {hasTags && (
              <div className="flex flex-wrap gap-1">
                <Tag className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400 mt-1" aria-hidden="true" />
                {displayTags.map((tag: string, index: number) => (
                  <span 
                    key={index} 
                    className="px-1.5 sm:px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full text-[10px] sm:text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
                {extraTagsCount > 0 && (
                  <span className="px-1.5 sm:px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] sm:text-xs">
                    +{extraTagsCount}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer avec appel à l'action */}
        <div className="bg-gradient-to-r from-gray-50 to-white py-1.5 sm:py-2 px-2 sm:px-4 border-t border-gray-100 flex justify-end mt-auto">
          <span className="text-xs sm:text-sm text-indigo-600 font-medium group-hover:text-indigo-800 transition-colors flex items-center">
            {t('public_decks.deck_details.view_deck')}
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-0.5 sm:ml-1 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </Link>
    </div>
  );
}